import { IMegaMenuActiveItemOverwrite, IMegaMenuItem } from '@/types/megaMenuTypes';
import router from '@/router';
export default function useMenu(): {
  getPathMatchWeight: (
    item: IMegaMenuItem,
    activeOverwrite: IMegaMenuActiveItemOverwrite | undefined,
  ) => number;
} {
  /**
   * Determines the match weight of a given menu item based on its path, controller, and action
   * compared to the current route and optional active overwrite.
   *
   * This is intended to be removed after the user facing FE is rewritten to new FE.
   * The weight system only makes sense when we don't know the backend path structure
   * and its edge conditions with asp net custom paths
   *
   * @param {IMegaMenuItem} item - The menu item whose path match weight is being calculated.
   * @param {IMegaMenuActiveItemOverwrite | undefined} activeOverwrite - Optional overwrite that
   * allows specifying an alternative controller and action to check against the item.
   * @return {number} The match weight for the given menu item. Higher values indicate a better match.
   */
  function getPathMatchWeight(
    item: IMegaMenuItem,
    activeOverwrite: IMegaMenuActiveItemOverwrite | undefined,
  ): number {
    const { path } = router.currentRoute.value;
    const ctrls = GetControllersRecursively(item);

    // No match cases
    if (item?.href == '#') {
      return 0;
    }

    // Exact matches - highest priority
    if (path == '/' && item?.action == 'WelcomeDashboard') {
      return 10;
    } else if (
      activeOverwrite &&
      item.controller?.toLowerCase() === activeOverwrite.controller?.toLowerCase() &&
      item.action?.toLowerCase() === activeOverwrite.action?.toLowerCase()
    ) {
      return 10;
    }

    // URL path matches - higher weight for more specific matches
    if (path != '/' && item.hasUrl) {
      try {
        // ASP NET CORE will skip the ` index ` part in the routing,
        // So Spaces/Index/14 will resolve to Spaces/14
        const cleanHref = item.href.replaceAll('/Index/', '/');
        const resolvedPath = router.resolve(cleanHref).path;
        if (resolvedPath.endsWith(path)) {
          // More specific paths get higher weight
          const pathSegments = path.split('/').filter((s) => s);
          return 7 + pathSegments.length;
        }
      } catch (error) {
        console.error('Error while calculating if menu item should be active', error);
      }
    }

    // Controller matches - medium priority
    if (ctrls.length > 0) {
      for (const ctrl of ctrls) {
        if (router.currentRoute.value.path.startsWith(`/${ctrl}`)) {
          return 5;
        }
      }
    }

    return 0;
  }

  function GetControllersRecursively(item: IMegaMenuItem): string[] {
    const ctrls: Set<string> = new Set();

    if (item.controller) {
      ctrls.add(item.controller);
    }

    if (item.hasItems && item.items?.length) {
      for (const link of item.items) {
        const childControllers = GetControllersRecursively(link);
        childControllers.forEach((ctrl) => ctrls.add(ctrl));
      }
    }

    return Array.from(ctrls);
  }

  return {
    getPathMatchWeight,
  };
}
